/**
 * R6 — AttachmentVisionAnalysis.jsx
 *
 * Lets the operator upload an image (photo of a pipe/valve/flange…) and
 * receive a structured analysis (part_type, DN, material, description).
 * Display the result as an enrichment card near the upload area.
 */
import { useRef, useState } from "react";
import {
  Camera,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "../lib/api";

const PART_TO_FAM_LABEL = {
  F01: "F01 Tubi",
  F02: "F02 Raccordi",
  F03: "F03 Raccordi saldati",
  F04: "F04 Flange",
  F05: "F05 Valvole",
  F06: "F06 Valvole regolazione",
  F08: "F08 Strumentazione",
  F11: "F11 Scambiatori",
  F12: "F12 Accessori",
};

export default function AttachmentVisionAnalysis({
  offerId,
  onAddVoce,
}) {
  const fileRef = useRef(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState("");

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !offerId) return;
    setFileName(f.name);
    setPreview(URL.createObjectURL(f));
    setAnalyzing(true);
    setResult(null);
    try {
      const d = await api.visionOcr(offerId, f);
      setResult(d.analysis || null);
      if (d.cached) toast.info("Analisi già presente (cache)");
      else toast.success("Foto analizzata");
    } catch (err) {
      toast.error(formatErrorDetail(err) || "Analisi fallita");
    } finally {
      setAnalyzing(false);
      e.target.value = ""; // allow re-upload same file
    }
  };

  const addVoce = () => {
    if (!result || !onAddVoce) return;
    const estratti = {};
    if (result.dn) {
      let dn = String(result.dn).toUpperCase().replace(/\s+/g, "");
      if (!dn.startsWith("DN")) dn = `DN${dn}`;
      estratti.P01_DN = dn;
    }
    if (result.material) estratti.P10_materiale = result.material;
    onAddVoce({
      famiglia: result.famiglia_suggerita || "F05",
      sotto_tipo: result.description || result.part_type || "",
      quantita: 1,
      parametri_estratti: estratti,
      parametri_inferiti: {},
      parametri_mancanti_critici: [],
      stato: "incomplete",
      candidati: [],
      _source: "vision_ocr",
    });
    toast.success("Voce aggiunta dalla foto");
    setResult(null);
    setPreview("");
    setFileName("");
  };

  return (
    <section
      className="rounded-md p-2 space-y-1.5 text-[11px]"
      style={{
        background: "var(--bg-surface)",
        border: "1px solid var(--border-subtle)",
      }}
      data-testid="attachment-vision"
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={onFile}
        className="hidden"
        data-testid="vision-file-input"
      />
      <button
        type="button"
        onClick={pick}
        disabled={analyzing || !offerId}
        className="w-full inline-flex items-center justify-center gap-1 h-6 rounded text-[11px] font-medium disabled:opacity-40"
        style={{
          background: "var(--accent-subtle)",
          color: "var(--accent-primary)",
          border: "1px solid #BFDBFE",
        }}
        data-testid="vision-trigger"
      >
        {analyzing ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin" /> Analisi in corso…
          </>
        ) : (
          <>
            <Camera className="h-3 w-3" /> Analizza foto
          </>
        )}
      </button>

      {preview && !analyzing && (
        <img
          src={preview}
          alt={fileName}
          className="w-full h-16 object-cover rounded"
          style={{ border: "1px solid var(--border-subtle)" }}
          data-testid="vision-preview"
        />
      )}

      {result && (
        <div
          className="rounded p-1.5 space-y-1"
          style={{
            background:
              (result.confidence_pct ?? 0) >= 60 ? "#F0FDF4" : "#FFFBEB",
            border:
              (result.confidence_pct ?? 0) >= 60
                ? "1px solid #BBF7D0"
                : "1px solid #FDE68A",
          }}
          data-testid="vision-result"
        >
          <div className="flex items-center gap-1 text-[10px] font-semibold"
               style={{
                 color: (result.confidence_pct ?? 0) >= 60
                   ? "#166534"
                   : "#92400E",
               }}>
            {(result.confidence_pct ?? 0) >= 60 ? (
              <CheckCircle2 className="h-2.5 w-2.5" />
            ) : (
              <AlertTriangle className="h-2.5 w-2.5" />
            )}
            Confidenza {result.confidence_pct}%
          </div>
          <div className="text-[11px]"
               style={{ color: "var(--text-primary)", lineHeight: 1.3 }}>
            {result.description || result.part_type}
          </div>
          <div className="flex flex-wrap gap-1">
            {result.famiglia_suggerita && (
              <Chip text={PART_TO_FAM_LABEL[result.famiglia_suggerita] ||
                          result.famiglia_suggerita} />
            )}
            {result.dn && <Chip text={`DN ${result.dn}`} />}
            {result.material && <Chip text={result.material} />}
            {result.brand && <Chip text={`Marca: ${result.brand}`} />}
          </div>
          {(result.codes_visible || []).length > 0 && (
            <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>
              Codici visibili: {result.codes_visible.slice(0, 3).join(", ")}
            </div>
          )}
          {(result.confidence_pct ?? 0) < 60 && (
            <div className="text-[10px]" style={{ color: "#92400E" }}>
              Analisi imprecisa — verifica manualmente.
            </div>
          )}
          <button
            type="button"
            onClick={addVoce}
            className="w-full inline-flex items-center justify-center gap-1 h-6 rounded text-[10px] font-medium text-white"
            style={{ background: "var(--accent-primary)" }}
            data-testid="vision-add-voce"
          >
            <Plus className="h-3 w-3" />
            Aggiungi voce con queste specs
          </button>
        </div>
      )}
    </section>
  );
}

function Chip({ text }) {
  return (
    <span
      className="inline-flex items-center px-1 py-0.5 rounded text-[10px]"
      style={{
        background: "var(--bg-subtle)",
        color: "var(--text-secondary)",
      }}
    >
      {text}
    </span>
  );
}
